/**
 * Marketplace outbound data allowlist (PHASE1-PRE-CATALOGUE-SECURITY-021A).
 *
 * Canonical Product Master is private by default. Provider HTTP bodies, job
 * payloads, and provider-bound publication snapshots may only contain keys
 * explicitly listed here. A newly added canonical column is unavailable to
 * eBay / BigCommerce / Amazon until it is deliberately added to a projection.
 *
 * Keep identical to supabase/functions/_shared/provider-outbound-allowlist.ts.
 */

export const SENSITIVE_OUTBOUND_SENTINELS = {
  cost: 987654.32,
  costText: "987654.32",
  supplier: "NEVER_SEND_SUPPLIER_021A",
  supplierSku: "PRIVATE-SUPPLIER-SKU-021A",
  internalNote: "CONFIDENTIAL_INTERNAL_NOTE_021A",
  margin: "PRIVATE_MARGIN_021A",
  futureField: "DO_NOT_LEAK_FUTURE_FIELD_021A",
} as const;

/** Public / provider-eligible product fields shared by selling channels. */
export const MARKETPLACE_PRODUCT_KEYS = [
  "id",
  "name",
  "description",
  "short_description",
  "sku",
  "barcode",
  "brand",
  "mpn",
  "condition",
  "product_type",
  "selling_price",
  "weight_kg",
  "length_cm",
  "width_cm",
  "height_cm",
  "category_id",
  "seo_title",
  "meta_description",
  "seo_keywords",
  "url_slug",
  "og_title",
  "og_description",
  "og_image_url",
  "storefront_visible",
  "is_featured",
  "min_purchase_qty",
  "max_purchase_qty",
] as const;

export const EBAY_PRODUCT_KEYS = MARKETPLACE_PRODUCT_KEYS;

export const BIGCOMMERCE_PRODUCT_KEYS = MARKETPLACE_PRODUCT_KEYS;

/** Dormant Amazon sketch — still allowlist-only; never publish. */
export const AMAZON_PRODUCT_KEYS = [
  ...MARKETPLACE_PRODUCT_KEYS,
  "manufacturer",
  "model_number",
  "country_of_origin",
  "hs_tariff_code",
] as const;

export const PROVIDER_VARIANT_KEYS = [
  "id",
  "sku",
  "barcode",
  "option_values",
  "selling_price",
  "weight_kg",
  "length_cm",
  "width_cm",
  "height_cm",
] as const;

export const CONNECTOR_OUTBOUND_KEYS = [
  "id",
  "org_id",
  "name",
  "provider",
  "marketplace_site",
  "auth_status",
  "enabled",
  "type",
  "status",
] as const;

export const EBAY_CONFIG_OUTBOUND_KEYS = [
  "id",
  "org_id",
  "connector_id",
  "marketplace_id",
  "currency",
  "environment",
  "seller_username",
] as const;

export const BC_CONFIG_OUTBOUND_KEYS = [
  "id",
  "org_id",
  "connector_id",
  "store_hash",
  "currency",
  "weight_unit",
  "dimension_unit",
  "catalog_visibility",
  "environment",
  "auth_status",
  "channel_id",
] as const;

export const LISTING_OUTBOUND_KEYS = [
  "id",
  "org_id",
  "connector_id",
  "product_id",
  "external_listing_id",
  "external_parent_id",
  "status",
  "listing_type",
  "channel_title",
  "channel_description",
  "channel_short_description",
  "channel_subtitle",
  "channel_condition_description",
  "channel_category_id",
  "channel_category_name",
  "currency",
  "price_strategy",
  "inventory_strategy",
  "media_strategy",
  "publication_status",
  "intended_version",
  "published_version",
  "dirty_domains",
  "channel_seo_title",
  "channel_meta_description",
  "channel_seo_keywords",
  "channel_url_slug",
  "channel_og_title",
  "channel_og_description",
  "channel_storefront_visible",
  "channel_is_featured",
] as const;

export const MEDIA_OUTBOUND_KEYS = [
  "media_asset_id",
  "storage_key",
  "url",
  "public_url",
  "src",
  "image_url",
  "original_filename",
  "mime_type",
  "width",
  "height",
  "alt_text",
  "role",
  "position",
  "is_primary",
  "source",
] as const;

const SECRET_KEY = /token|secret|password|authorization|credential|api[_-]?key|refresh|bearer/i;

const PRIVATE_CUSTOM_FIELD = /cost|supplier|margin|markup|profit|purchase|reorder|internal|credential|token|secret/i;

export type MarketplaceProvider = "ebay" | "bigcommerce" | "amazon";

export function productKeysForProvider(provider: MarketplaceProvider): readonly string[] {
  if (provider === "amazon") return AMAZON_PRODUCT_KEYS;
  if (provider === "bigcommerce") return BIGCOMMERCE_PRODUCT_KEYS;
  return EBAY_PRODUCT_KEYS;
}

export function pickAllowlisted(
  src: Record<string, unknown> | null | undefined,
  keys: readonly string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!src) return out;
  const allow = new Set(keys);
  for (const key of allow) {
    if (Object.prototype.hasOwnProperty.call(src, key) && src[key] !== undefined) {
      out[key] = src[key];
    }
  }
  return out;
}

export function pickProductForProvider(
  provider: MarketplaceProvider,
  product: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  return pickAllowlisted(product, productKeysForProvider(provider));
}

export function pickVariantForProvider(variant: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return pickAllowlisted(variant, PROVIDER_VARIANT_KEYS);
}

export function pickConnectorForProvider(connector: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return pickAllowlisted(connector, CONNECTOR_OUTBOUND_KEYS);
}

export function pickEbayConfigForProvider(config: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return pickAllowlisted(config, EBAY_CONFIG_OUTBOUND_KEYS);
}

export function pickBcConfigForProvider(config: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return pickAllowlisted(config, BC_CONFIG_OUTBOUND_KEYS);
}

export function pickListingForProvider(listing: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return pickAllowlisted(listing, LISTING_OUTBOUND_KEYS);
}

export function pickMediaForProvider(media: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return pickAllowlisted(media, MEDIA_OUTBOUND_KEYS);
}

export function isProviderEligibleProductField(sourceKey: string | null | undefined): boolean {
  if (!sourceKey) return false;
  return (MARKETPLACE_PRODUCT_KEYS as readonly string[]).includes(sourceKey)
    || (AMAZON_PRODUCT_KEYS as readonly string[]).includes(sourceKey);
}

export function filterProviderCustomFields(
  fields: Array<{ name: string; value: string }>,
): Array<{ name: string; value: string }> {
  return fields.filter((f) => !PRIVATE_CUSTOM_FIELD.test(f.name));
}

export function buildBcPriceSyncBody(sellingPrice: number): { price: number } {
  return { price: sellingPrice };
}

export function buildEbayPriceQuantitySyncBody(opts: {
  sku: string;
  offerId?: string | null;
  sellingPrice: number;
  currency?: string;
}): { requests: Array<Record<string, unknown>> } {
  const price = { value: Number(opts.sellingPrice).toFixed(2), currency: opts.currency || "AUD" };
  const request: Record<string, unknown> = { sku: opts.sku };
  if (opts.offerId) {
    request.offers = [{ offerId: opts.offerId, price }];
  }
  return { requests: [request] };
}

export function buildBcInventorySyncBody(availableQty: number): { inventory_level: number } {
  return { inventory_level: Math.max(0, availableQty) };
}

export function buildEbayInventoryQuantitySyncBody(opts: {
  sku: string;
  offerId?: string | null;
  availableQty: number;
}): { requests: Array<Record<string, unknown>> } {
  const qty = Math.max(0, opts.availableQty);
  const request: Record<string, unknown> = {
    sku: opts.sku,
    shipToLocationAvailability: { quantity: qty },
  };
  if (opts.offerId) {
    request.offers = [{ offerId: opts.offerId, availableQuantity: qty }];
  }
  return { requests: [request] };
}

export function projectProviderBoundSnapshot(
  provider: MarketplaceProvider,
  raw: Record<string, unknown>,
): Record<string, unknown> {
  const product = pickProductForProvider(provider, asRecord(raw.product));
  const connector = pickConnectorForProvider(asRecord(raw.connector));
  const variants = Array.isArray(raw.variants)
    ? raw.variants.map((row) => projectVariantRow(asRecord(row)))
    : [];
  const media = Array.isArray(raw.media)
    ? raw.media.map((row) => pickMediaForProvider(asRecord(row)))
    : [];
  const out: Record<string, unknown> = {
    success: raw.success,
    listing: pickListingForProvider(asRecord(raw.listing)),
    product,
    connector,
    variants,
    media,
    options: raw.options,
    identifiers: raw.identifiers,
    identifier_policy: raw.identifier_policy,
    storefront: raw.storefront,
    currency: raw.currency,
    marketplace_id: raw.marketplace_id,
    intended_version: raw.intended_version,
  };
  if (provider === "ebay") {
    out.ebay_config = pickEbayConfigForProvider(asRecord(raw.ebay_config));
    out.aspect_mappings = raw.aspect_mappings;
    out.connector_id = raw.connector_id;
    out.is_variation = raw.is_variation;
    out.variant_count = raw.variant_count;
    out.readiness = raw.readiness;
    out.steps = raw.steps;
  }
  if (provider === "bigcommerce") {
    out.bc_config = pickBcConfigForProvider(asRecord(raw.bc_config));
    out.custom_fields = Array.isArray(raw.custom_fields)
      ? filterProviderCustomFields(
        (raw.custom_fields as Array<{ name?: unknown; value?: unknown }>).map((f) => ({
          name: String(f?.name ?? ""),
          value: String(f?.value ?? ""),
        })),
      )
      : [];
    out.brand_cache = raw.brand_cache;
    out.attribute_values = raw.attribute_values;
  }
  return out;
}

function projectVariantRow(row: Record<string, unknown> | null): Record<string, unknown> {
  if (!row) return {};
  const nested = pickVariantForProvider(asRecord(row.variant));
  return {
    id: row.id,
    variant_id: row.variant_id,
    included: row.included,
    channel_sku: row.channel_sku,
    sku: row.sku,
    channel_price: row.channel_price,
    effective_channel_price: row.effective_channel_price,
    selling_price: row.selling_price,
    available_quantity: row.available_quantity,
    option_values: row.option_values,
    image_url: row.image_url,
    weight_kg: row.weight_kg,
    length_cm: row.length_cm,
    width_cm: row.width_cm,
    height_cm: row.height_cm,
    variant: nested,
  };
}

export function redactProviderObservability(value: unknown, depth = 0): unknown {
  if (depth > 10 || value == null) return value;
  if (typeof value === "string") {
    if (SECRET_KEY.test(value) || /(?:Atza|Atzr)\|/.test(value)) return "[redacted]";
    return value;
  }
  if (typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map((item) => redactProviderObservability(item, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (SECRET_KEY.test(key)) {
      out[key] = "[redacted]";
      continue;
    }
    if (isPrivateCanonicalKey(key)) continue;
    out[key] = redactProviderObservability(item, depth + 1);
  }
  return out;
}

export function isPrivateCanonicalKey(key: string): boolean {
  const k = key.toLowerCase();
  return (
    k === "cost_price"
    || k === "cost"
    || k.includes("supplier")
    || k.includes("purchase")
    || k === "margin"
    || k === "markup"
    || k === "profit"
    || k === "reorder_point"
    || k === "reorder_qty"
    || k === "tax_config"
    || k === "preferred_supplier_id"
    ||     k === "internal_notes"
    || k === "notes"
    || k === "credential_ref"
    || k === "credentials_encrypted"
    || k === "auth_token_ref"
    || k === "token_metadata"
    || k === "future_sensitive_field"
    || k === "config"
  );
}

export function serializedContainsSentinel(value: unknown, sentinel: string | number): boolean {
  return walkContains(value, sentinel, 0);
}

export function assertOutboundExcludesSentinels(value: unknown, extra: Array<string | number> = []): void {
  const sentinels: Array<string | number> = [
    SENSITIVE_OUTBOUND_SENTINELS.cost,
    SENSITIVE_OUTBOUND_SENTINELS.costText,
    SENSITIVE_OUTBOUND_SENTINELS.supplier,
    SENSITIVE_OUTBOUND_SENTINELS.supplierSku,
    SENSITIVE_OUTBOUND_SENTINELS.internalNote,
    SENSITIVE_OUTBOUND_SENTINELS.margin,
    SENSITIVE_OUTBOUND_SENTINELS.futureField,
    ...extra,
  ];
  const blob = stableSerialize(value);
  for (const s of sentinels) {
    const needle = String(s);
    if (blob.includes(needle)) {
      throw new Error(`Outbound payload leaked sentinel ${needle}`);
    }
  }
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? v as Record<string, unknown> : null;
}

function walkContains(value: unknown, sentinel: string | number, depth: number): boolean {
  if (depth > 12 || value == null) return false;
  if (typeof sentinel === "number" && typeof value === "number") {
    return Math.abs(value - sentinel) < 0.0001;
  }
  if (typeof value === "string") return value.includes(String(sentinel));
  if (typeof value === "number" || typeof value === "boolean") return String(value) === String(sentinel);
  if (Array.isArray(value)) return value.some((item) => walkContains(item, sentinel, depth + 1));
  if (typeof value === "object") {
    for (const [k, item] of Object.entries(value as Record<string, unknown>)) {
      if (k.includes(String(sentinel))) return true;
      if (walkContains(item, sentinel, depth + 1)) return true;
    }
  }
  return false;
}

function stableSerialize(value: unknown): string {
  try {
    return JSON.stringify(value) ?? "";
  } catch {
    return String(value);
  }
}
